import { FinishEnum, GameCard, NONFOIL_FINISH } from "modules/cards/model/card";
import { CardPreviewLoading } from "modules/common/components/CardPreviewLoading";
import { ResponsiveCardSizing } from "utils/styles";
import { CardSetPrice } from "modules/common/components/CardDetailsDialog/CardSetPrice";
import {
  CardFinishPrices,
  orderedFinishes,
} from "modules/cards/components/CardFinishPrices";
import { useSets } from "modules/common/hooks/useSets";
import { finishLabel, hasFoilVisual } from "modules/cards/model/finishBehavior";

interface ChangePrintingCardDetailsProps {
  card: GameCard;
  changePrinting: (printing: GameCard, finish: FinishEnum) => (e: any) => void;
  disabled?: boolean;
  /** Forwarded to CardSetPrice so its set link closes the hosting dialog. */
  onNavigate?: () => void;
}

export function CardChangePrintingCardDetails({
  card,
  changePrinting,
  disabled = false,
  onNavigate,
}: ChangePrintingCardDetailsProps) {
  // The preview visual follows registry metadata; the per-finish Select buttons
  // below disambiguate the exact finish persisted to the deck.
  const isFoil = hasFoilVisual(card.finishes);
  const { getSetName } = useSets();
  const setName = getSetName(card.setCode, card.game);
  // A printing with no advertised finishes still needs one selection path
  // (the old image-as-button always gave one); default to a plain nonfoil Select.
  // orderedFinishes keeps these aligned with the CardFinishPrices row above
  // while still rendering a button for every finish the printing offers.
  const finishes =
    card.finishes.length > 0
      ? orderedFinishes(card.finishes)
      : [NONFOIL_FINISH];

  return (
    <div className="rounded-bl-box rounded-br-box group relative">
      <div className="block w-full">
        <CardPreviewLoading
          sizing={ResponsiveCardSizing}
          card={card}
          foil={isFoil}
        />
      </div>
      <div className="rounded-b-box border-base-content/20 bg-base-100 -mt-4 w-full border p-2 pt-2">
        <h3 className="sr-only">{card.name}</h3>
        <div className="flex flex-col gap-1.5 pt-2 lg:pt-3">
          <CardSetPrice card={card} hidePrice onNavigate={onNavigate} />
          <CardFinishPrices card={card} className="text-sm font-medium" />
          <div className="flex flex-wrap gap-1">
            {finishes.map((finish) => {
              const isNonFoil = finish === NONFOIL_FINISH;
              const label = finishLabel(finish);
              return (
                <button
                  key={finish}
                  onClick={changePrinting(card, finish)}
                  disabled={disabled}
                  className="btn btn-sm btn-primary flex-1"
                  aria-label={
                    isNonFoil
                      ? `Select ${card.name} from ${setName}`
                      : `Select ${label} ${card.name} from ${setName}`
                  }
                >
                  {isNonFoil ? "Select" : `Select ${label}`}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
